import { useEffect, useId, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { CustomValidation, ErrorMessages, type Question, Rules } from '../../config/types';
import { useFormData } from '../../hooks/useFormData';
import {
  getErrorMessage,
  groupValidation,
  objectToString,
  stringToObject,
} from '../../utils/form-utils';
import GradeInput from './DateInputField/GradeInput';
import GradeInputColumn from './DateInputField/GradeInputColumn';
import { GradeInputWrapper } from './DateInputField/GradeInputField';
import {
  dateIsEmpty,
  dateIsFullyPopulated,
  getMinYearValue,
  invalidDateErrorMessage,
  isMinYearRuleValid,
  isValidDate,
} from './DateInputField/utils';
import Details from './Details';
import ErrorMessage from './ErrorMessage';

interface GradeInputProps {
  name: string;
  defaultValue?: string; // An ISO date string with only the yyyy-mm-dd
  disabled?: boolean;
  dayLabel?: string;
  monthLabel?: string;
  yearLabel?: string;
  anchorId?: string;
  label?: string;
  onChange?(value: string): void;
}

export const getRows = () => ['scs4', 'scs3', 'scs2', 'scs1'];
export const getColumns = () => [];
export const getKeys = () =>
  getRows().flatMap((row) => getColumns().map((column) => `${row}${column}`));

export function GradeInputWrapperColumn({
  defaultValue,
  disabled = false,
  label,
  anchorId = useId(),
  onChange,
}: GradeInputProps) {
  const initialValues = useMemo(() => stringToObject(defaultValue), [defaultValue]);

  const [values, setValues] = useState(initialValues);

  const triggerChange = (updatedValues: typeof values) => {
    if (onChange) {
      const valueString = objectToString(updatedValues);
      if (dateIsEmpty(valueString)) {
        onChange('');
      } else {
        onChange(valueString);
      }
    }
  };

  const handleValueChange = (field: keyof typeof values) => (newValue: string) => {
    const updatedValues = { ...values, [field]: newValue };
    setValues(updatedValues);
    triggerChange(updatedValues);
  };

  const generateInputId = (suffix: string) => `${anchorId}-${suffix}`;

  const rows = useMemo(
    () => [
      { grade: 'SCS4', fields: ['scs4'] },
      { grade: 'SCS3', fields: ['scs3'] },
      { grade: 'SCS2', fields: ['scs2'] },
      { grade: 'SCS1', fields: ['scs1'] },
    ],
    [],
  );

  return (
    <div>
      <table className="govuk-table">
        <thead className="govuk-table__head">
          <tr className="govuk-table__row">
            <th scope="col" className="govuk-table__header">
              Grade
            </th>
            <th scope="col" className="govuk-table__header">
              {label}
            </th>
          </tr>
        </thead>
        <tbody className="govuk-table__body">
          {rows.map(({ grade, fields }) => (
            <tr key={grade} className="govuk-table__row">
              <th scope="row" className="govuk-table__header">
                {grade}
              </th>
              {fields.map((field) => (
                <td key={field} className="govuk-table__cell">
                  <GradeInputColumn
                    testId={`${field}-${grade.toLowerCase().replace(' ', '-')}`}
                    disabled={disabled}
                    anchorId={generateInputId(field)}
                    onChange={handleValueChange(field as keyof typeof values)} // Cast field to a valid key
                    value={values[field as keyof typeof values]} // Cast field to a valid key
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface GradeInputFieldProps {
  question: Question;
  disabled?: boolean;
  anchorId: string;
  detailsHeading?: string;
  detailsContent?: string;
  label?: string;
}

export function InputExternalExemptions({
  question,
  disabled,
  anchorId,
  detailsHeading,
  detailsContent,
  label,
}: GradeInputFieldProps) {
  const { register, getFieldState, getValues, setValue } = useFormContext();
  const { error } = getFieldState(question.name);

  useEffect(() => {
    // If the value is currently not defined in the form, initialise it
    if (getValues(question.name) === undefined) {
      setValue(question.name, '');
    }
  }, []);

  const { data } = useFormData();
  const { onChange, name } = register(question.name, {
    validate: (value: string) =>
      groupValidation({
        value: stringToObject(value),
        answers: data?.answers,
        question,
      }),
  });

  const handleChange = (dateChange: string) => {
    onChange({ target: { name, value: dateChange } });
  };

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <div className="govuk-date-input">
        <GradeInputWrapperColumn
          name={question.name}
          disabled={disabled}
          anchorId={anchorId}
          label={label}
          onChange={handleChange}
          defaultValue={getValues(question.name)}
        />
      </div>
    </>
  );
}
